/**
 * Every public page of a language with its kind, per "Inventory per language" and rules 1, 2 and 8-10 of
 * "Canonical and hreflang" in docs/specs/prerender-and-language-urls.md. The build script that writes the
 * page manifest (run with vite-node) and the client head hook read it; sitemaps and hreflang sets are the
 * 'indexable' entries of the live languages.
 *
 * buildPageInventory is pure and takes its data as parameters; pageInventory feeds it the bundled
 * catalog.json and the file names of src/content/blog. Imports are relative (no `~/` alias), as in
 * src/entities/urls/localised-path.ts.
 */
import catalogJson from '../../data/catalog.json';
import { type LanguageCode, languageCodes } from '../../texts/languages';
import { productPath } from '../catalog/paths';
import type { Catalog } from '../catalog/types';
import { isProductParent } from '../catalog/variants';

/**
 * - indexable: answers 200, canonical to itself, in the sitemap and in hreflang sets.
 * - noindex: answers 200 with `noindex`, never in a sitemap or hreflang set.
 * - canonical-elsewhere: answers 200 with `canonical` to canonicalInternalPath (same language), no hreflang.
 * - redirect: answers 301 to redirectTo.
 */
export type PageKind = 'indexable' | 'noindex' | 'canonical-elsewhere' | 'redirect';

export type PageInventoryEntry = {
  /** A path of the route tree with English words, as toLocalisedPath takes it, e.g. '/products/ja-150m'. */
  internalPath: string;
  kind: PageKind;
  /** The languages the page exists in: all for catalog pages, the languages with an `.mdx` for an article. */
  availableLanguages: readonly LanguageCode[];
  /** Set for 'canonical-elsewhere': the internal path the page is canonical to. */
  canonicalInternalPath?: string;
  /** Set for 'redirect': the internal path it redirects to. */
  redirectTo?: string;
};

export type PageInventoryData = {
  catalog: Pick<Catalog, 'categories' | 'categoryPages' | 'subcategoryPages' | 'products'>;
  /** Blog article slug → the languages that have its `src/content/blog/<slug>.<lang>.mdx`. */
  blog: Record<string, readonly LanguageCode[]>;
};

/** The pages of one language, from the given catalog and blog articles. */
export function buildPageInventory(lang: LanguageCode, { catalog, blog }: PageInventoryData): PageInventoryEntry[] {
  const page = (internalPath: string, kind: PageKind = 'indexable'): PageInventoryEntry => ({
    internalPath,
    kind,
    availableLanguages: languageCodes,
  });

  const categorySlugs = new Map(catalog.categories.map((category) => [category.id, category.slug || category.id]));
  const categoryPath = (categoryId: string) => `/categories/${categorySlugs.get(categoryId) ?? categoryId}`;
  // The categories whose subcategory pages only group their inline product listing: the subcategory
  // route redirects them to the category (src/routes/categories_.$categoryId.$subcategoryId.tsx).
  const inlineListing = new Set(
    catalog.categoryPages.filter((categoryPage) => categoryPage.showInlineProductListing).map(({ id }) => id),
  );
  const parents = catalog.products.filter(isProductParent);

  const subcategories = catalog.subcategoryPages.map((subcategory): PageInventoryEntry => {
    const categoryUrl = categoryPath(subcategory.categoryId);
    const internalPath = `${categoryUrl}/${subcategory.slug || subcategory.id}`;
    if (inlineListing.has(subcategory.categoryId)) {
      return { ...page(internalPath, 'redirect'), redirectTo: categoryUrl };
    }
    // Without products the page is an empty listing (1_9, 2_11), unless it is an app landing page with
    // its own content instead of a listing (7_0, 7_1).
    const empty = !subcategory.landing && !parents.some((product) => product.subcategoryIds.includes(subcategory.id));

    return page(internalPath, empty ? 'noindex' : 'indexable');
  });

  // Colour variants are canonical to their parent (seo-meta-texts.md).
  const products = catalog.products.map(
    (product): PageInventoryEntry =>
      isProductParent(product)
        ? page(productPath(product.id))
        : {
            ...page(productPath(product.id), 'canonical-elsewhere'),
            canonicalInternalPath: productPath(product.parentId as string),
          },
  );

  const articles = Object.entries(blog)
    .filter(([, languages]) => languages.includes(lang))
    .map(([slug, languages]) => ({
      internalPath: `/blog/${slug}`,
      kind: 'indexable' as const,
      availableLanguages: languageCodes.filter((code) => languages.includes(code)),
    }));

  return [
    page('/'),
    page('/products'),
    page('/compatibility'),
    page('/blog'),
    page('/search', 'noindex'),
    ...catalog.categories.map((category) => page(categoryPath(category.id))),
    ...subcategories,
    ...products,
    ...articles,
  ];
}

const BLOG_FILENAME = new RegExp(`/([^/]+)\\.(${languageCodes.join('|')})\\.mdx$`);

/** Blog article slug → the languages of its `.mdx` files, from file names like `<slug>.<lang>.mdx`. */
export function blogLanguages(files: string[]): Record<string, LanguageCode[]> {
  const bySlug: Record<string, LanguageCode[]> = {};
  for (const file of files) {
    const match = file.match(BLOG_FILENAME);
    if (match) {
      (bySlug[match[1]] ??= []).push(match[2] as LanguageCode);
    }
  }

  return bySlug;
}

let inventoryData: PageInventoryData | undefined;

/**
 * The pages of one language, from the bundled catalog.json and src/content/blog.
 *
 * The glob is read here, not at module level, so a bundle that imports only buildPageInventory (the client,
 * src/entities/seo/use-document-head.ts) leaves it out: it compiles to a loader per article and language (~40 kB).
 */
export function pageInventory(lang: LanguageCode): PageInventoryEntry[] {
  inventoryData ??= {
    catalog: catalogJson as unknown as Catalog,
    // Only the keys are read: a lazy glob loads no article, so neither its body nor its meta is compiled or
    // bundled for the inventory (the client's lazy blog chunks are the same modules, src/entities/blog/api.ts).
    blog: blogLanguages(Object.keys(import.meta.glob('/src/content/blog/*.mdx'))),
  };

  return buildPageInventory(lang, inventoryData);
}
